import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';
import useApprovalKeyboardShortcuts from '@/hooks/aiTools/useApprovalKeyboardShortcuts';
import { Flex, spaces } from '@electricjs/arc';
import { useDefaultTool } from '@copilotkit/react-core';
import { AIToolChip } from '@/components/AIChat/AIToolChip';
import { ApprovalButtons } from '@/components/AIChat/ApprovalButtons';
import { ToolDisplayStatus } from './types';
import type { PendingToolApproval } from '@/hooks/aiChat/useCopilotKitAgent';
import { HIDDEN_TOOL_NAMES, COPILOT_STATUS } from './constants';

/**
 * Type guard to check if a tool name should be hidden
 */
const isHiddenTool = (toolName: string): boolean => {
  return HIDDEN_TOOL_NAMES.some(hiddenName => hiddenName === toolName);
};

type UseDefaultToolRendererOptions = {
  pendingToolApproval?: PendingToolApproval | null;
  respondToApproval?: (toolCallId: string, approved: boolean) => void;
  /**
   * Ref to the set of tool function names that have a confirmed ToolMessage
   * result in the current messages array. Used to show restored tool call
   * chips as "completed" instead of "pending" — CopilotKit does not
   * reconstruct tool status from ToolMessage objects when setMessages is called.
   */
  completedToolNamesRef?: RefObject<Set<string>>;
};

/**
 * Custom hook for rendering tools without specific renderers.
 * Provides a catch-all renderer that displays tool chips for any tool call.
 *
 * When a backend-deferred tool call is awaiting approval (pendingToolApproval),
 * the renderer also shows ApprovalButtons inline — matching the pattern used by
 * useHumanInTheLoop-based frontend tools.
 */
export const useDefaultToolRenderer = ({
  pendingToolApproval,
  respondToApproval,
  completedToolNamesRef,
}: UseDefaultToolRendererOptions = {}) => {
  // Use refs so the memoized render function always reads the latest values
  // without needing to be recreated (which would re-register the tool).
  const pendingApprovalRef = useRef(pendingToolApproval);
  const respondRef = useRef(respondToApproval);
  // Ref to the latest completed-tool-names set — updated by the caller synchronously
  // on each render so our stable renderDefaultTool callback always reads fresh data.
  const completedNamesRef = useRef<Set<string>>(new Set());

  // Sync refs synchronously during render (not in a useEffect) so that
  // renderDefaultTool reads the latest values on the same render cycle that
  // produced them. A useEffect would lag one cycle, causing approval buttons
  // to miss the render triggered by the tool_requires_approval event.
  pendingApprovalRef.current = pendingToolApproval;
  respondRef.current = respondToApproval;
  if (completedToolNamesRef) {
    completedNamesRef.current = completedToolNamesRef.current ?? new Set();
  }

  // Keyboard shortcuts: Cmd/Ctrl+Enter to approve, Cmd/Ctrl+Backspace to reject.
  // Only registered while a backend-deferred approval is actually pending —
  // otherwise Cmd+Backspace and Ctrl+Backspace/Delete would be permanently
  // intercepted and their default text-editing behaviour suppressed.
  useApprovalKeyboardShortcuts({
    onApprove: () => {
      const pending = pendingApprovalRef.current;
      const respond = respondRef.current;
      if (!pending || !respond) return;
      respond(pending.toolCallId, true);
    },
    onDeny: () => {
      const pending = pendingApprovalRef.current;
      const respond = respondRef.current;
      if (!pending || !respond) return;
      respond(pending.toolCallId, false);
    },
    enabled: !!pendingToolApproval && !!respondToApproval,
  });

  const renderDefaultTool = useCallback(
    ({
      name,
      args,
      status,
    }: {
      name: string;
      args: Record<string, unknown>;
      status: string;
      result?: unknown;
    }) => {
      // Filter out internal tools that shouldn't be displayed
      if (isHiddenTool(name)) {
        return <></>;
      }

      const pending = pendingApprovalRef.current;
      const respond = respondRef.current;
      // Only non-complete chips can be awaiting approval. This prevents:
      // 1) Historical chips with the same tool name from showing approval buttons.
      // 2) A deadlock when toolCallName is unknown — the active (non-complete) chip
      //    shows the buttons regardless of name.
      const isCurrentlyPending = status !== COPILOT_STATUS.COMPLETE;
      const isAwaitingApproval =
        !!pending &&
        isCurrentlyPending &&
        (!pending.toolCallName || pending.toolCallName === name);

      // Map CopilotKit status to ToolDisplayStatus.
      // EXECUTING is checked first so a live in-progress call always shows as
      // RUNNING, even when a prior call to the same tool is in completedNamesRef.
      // hasConfirmedResult handles the restored-message case: CopilotKit reports
      // 'inProgress' for completed tool calls when setMessages is called (it
      // doesn't reconstruct live-run state from ToolMessage objects), so we fall
      // back to checking completedNamesRef — built from AssistantMessage.toolCalls
      // × ToolMessage pairs — to show the correct checkmark.
      const hasConfirmedResult = completedNamesRef.current.has(name);
      let displayStatus: ToolDisplayStatus;
      if (isAwaitingApproval) {
        displayStatus = ToolDisplayStatus.NEEDS_APPROVAL;
      } else if (status === COPILOT_STATUS.EXECUTING) {
        displayStatus = ToolDisplayStatus.RUNNING;
      } else if (status === COPILOT_STATUS.COMPLETE || hasConfirmedResult) {
        displayStatus = ToolDisplayStatus.COMPLETED;
      } else {
        displayStatus = ToolDisplayStatus.PENDING;
      }

      return (
        <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
          <AIToolChip
            id={`default-${Date.now()}`}
            toolSlug={name}
            toolArgs={JSON.stringify(args)}
            variant={displayStatus}
          />
          {isAwaitingApproval && respond && pending && (
            <ApprovalButtons
              onApprove={() => respond(pending.toolCallId, true)}
              onReject={() => respond(pending.toolCallId, false)}
              style={{ flexDirection: 'row' }}
            />
          )}
        </Flex>
      );
    },
    []
  );

  useDefaultTool({
    render: renderDefaultTool,
  });
};
